import { SourcingChannelStatus } from 'api-clients/monolith';
import { FormattedMessage } from 'react-intl';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Typography } from '@/components/ui/typography';

import { ChannelStatusTag } from '.';

interface StatusRowProps {
  status: SourcingChannelStatus;
  boldText: React.ReactNode;
  description: React.ReactNode;
}

function StatusRow({ status, boldText, description }: StatusRowProps) {
  return (
    <div className="flex items-start gap-[19px]">
      <div className="w-24 shrink-0">
        <ChannelStatusTag status={status} />
      </div>
      <Typography variant="inactive1" className="flex-1">
        <Typography variant="subtitle2" as="span">
          {boldText}
        </Typography>{' '}
        {description}
      </Typography>
    </div>
  );
}

function Divider() {
  return <div className="bg-fx-neutral-200 h-px w-full shrink-0" />;
}

interface CampaignStatusGuideDialogProps {
  children: React.ReactNode;
}

export function CampaignStatusGuideDialog({
  children,
}: CampaignStatusGuideDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="w-[480px] max-w-[480px] p-0">
        <DialogHeader>
          <DialogTitle>
            <FormattedMessage
              defaultMessage="Campaign status guide"
              description="Title of the modal explaining each campaign status meaning"
            />
          </DialogTitle>
          <DialogDescription className="sr-only">
            <FormattedMessage
              defaultMessage="Explains what each campaign status means"
              description="Screen-reader description for the campaign status guide dialog"
            />
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-6 p-6">
          <StatusRow
            status="in_progress"
            boldText={
              <FormattedMessage
                defaultMessage="Submitted, not live yet."
                description="Bold heading in status guide for 'Publishing' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="The campaign has been sent to the job board and is being processed — this usually takes a few hours. No action needed."
                description="Description in status guide for 'Publishing' status"
              />
            }
          />
          <Divider />
          <StatusRow
            status="scheduled"
            boldText={
              <FormattedMessage
                defaultMessage="Waiting for its start date."
                description="Bold heading in status guide for 'Scheduled' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="The campaign is set up and will go live automatically on the scheduled date."
                description="Description in status guide for 'Scheduled' status"
              />
            }
          />
          <Divider />
          <StatusRow
            status="active"
            boldText={
              <FormattedMessage
                defaultMessage="Live and receiving applicants."
                description="Bold heading in status guide for 'Active' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="The job is published on the board and candidates can apply. Use the toggle to pause anytime."
                description="Description in status guide for 'Active' status"
              />
            }
          />
          <Divider />
          <StatusRow
            status="stopped"
            boldText={
              <FormattedMessage
                defaultMessage="Paused by you or by an automated rule."
                description="Bold heading in status guide for 'Paused' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="The job is hidden from the board, and remaining budget and days are preserved. Use the toggle to resume anytime."
                description="Description in status guide for 'Paused' status"
              />
            }
          />
          <Divider />
          <StatusRow
            status="completed"
            boldText={
              <FormattedMessage
                defaultMessage="Campaign has ended."
                description="Bold heading in status guide for 'Completed' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="Either the budget was spent or the end date passed."
                description="Description in status guide for 'Completed' status"
              />
            }
          />
          <Divider />
          <StatusRow
            status="failed"
            boldText={
              <FormattedMessage
                defaultMessage="The campaign has failed to launch."
                description="Bold heading in status guide for 'Failed' status"
              />
            }
            description={
              <FormattedMessage
                defaultMessage="No budget was spent. Try republishing, or contact support if it happens again."
                description="Description in status guide for 'Failed' status"
              />
            }
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
